"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useTheme } from "next-themes";
import { useMutation } from "@tanstack/react-query";

import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import {
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
} from "@/components/ui/modal";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { customInstance } from "@/lib/api/client";
import { ApiError } from "@/lib/api/error";
import { cn } from "@/lib/utils";
import {
  SpotlightHighlight,
  SpotlightShimmer,
  SpotlightTypewriter,
  parseSpotlightMarkdown,
} from "./spotlight";
import { Alert } from "@/components/ui/alert";

export type SummaryMode = "bullets" | "paragraph" | "detailed";

type ModeIcon = "IconListBullets" | "IconCodeLines" | "IconListSparkle";

const MODES: ReadonlyArray<{
  value: SummaryMode;
  label: string;
  icon: ModeIcon;
}> = [
  { value: "bullets", label: "Bullet Points", icon: "IconListBullets" },
  { value: "paragraph", label: "Short Paragraph", icon: "IconCodeLines" },
  { value: "detailed", label: "Detailed Bullets", icon: "IconListSparkle" },
];

const DEFAULT_MODE: SummaryMode = "bullets";

interface ProfileSummaryResponse {
  profile_id: string;
  mode: SummaryMode;
  summary: string;
}

// Thin wrapper around the existing API client. Once Orval is regenerated
// against the backend's new endpoint, swap this for `summarizeProfile` from
// `@/lib/api/generated`.
async function summarizeProfileRequest(
  profileId: string,
  mode: SummaryMode,
  options?: RequestInit,
): Promise<ProfileSummaryResponse> {
  return customInstance<ProfileSummaryResponse>(
    `/api/profiles/${encodeURIComponent(profileId)}/summarize?mode=${mode}`,
    {
      ...options,
      method: "POST",
    },
  );
}

function isAbortError(err: unknown): boolean {
  return err instanceof DOMException && err.name === "AbortError";
}

function errorMessageFrom(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return "Something went wrong generating the summary.";
}

export interface ProfileSummarizeProps {
  profileId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function ProfileSummarize({
  profileId,
  open,
  onOpenChange,
}: ProfileSummarizeProps) {
  const [activeMode, setActiveMode] = useState<SummaryMode>(DEFAULT_MODE);
  const [summaries, setSummaries] = useState<
    Partial<Record<SummaryMode, string>>
  >({});
  const [loadingMode, setLoadingMode] = useState<SummaryMode | null>(null);
  const [errors, setErrors] = useState<Partial<Record<SummaryMode, string>>>(
    {},
  );
  const [copied, setCopied] = useState(false);

  const abortRef = useRef<AbortController | null>(null);
  const cacheRef = useRef(summaries);
  cacheRef.current = summaries;

  const mutation = useMutation({
    mutationFn: (vars: { mode: SummaryMode; signal: AbortSignal }) =>
      summarizeProfileRequest(profileId, vars.mode, { signal: vars.signal }),
  });

  const fetchSummary = useCallback(
    async (mode: SummaryMode, { force = false }: { force?: boolean } = {}) => {
      if (!force && cacheRef.current[mode] !== undefined) return;

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setLoadingMode(mode);
      setErrors((prev) => {
        if (prev[mode] === undefined) return prev;
        const next = { ...prev };
        delete next[mode];
        return next;
      });

      try {
        const result = await mutation.mutateAsync({
          mode,
          signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        setSummaries((prev) => ({ ...prev, [mode]: result.summary }));
      } catch (err) {
        if (controller.signal.aborted || isAbortError(err)) return;
        setErrors((prev) => ({ ...prev, [mode]: errorMessageFrom(err) }));
      } finally {
        if (abortRef.current === controller) {
          abortRef.current = null;
          setLoadingMode((current) => (current === mode ? null : current));
        }
      }
    },
    [mutation],
  );

  useEffect(() => {
    if (!open) return;
    setActiveMode(DEFAULT_MODE);
    setSummaries({});
    setErrors({});
    setCopied(false);
    void fetchSummary(DEFAULT_MODE, { force: true });
    return () => {
      abortRef.current?.abort();
      abortRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleTabChange = useCallback(
    (next: string) => {
      const mode = next as SummaryMode;
      setActiveMode(mode);
      setCopied(false);
      void fetchSummary(mode);
    },
    [fetchSummary],
  );

  const handleRegenerate = useCallback(() => {
    setCopied(false);
    void fetchSummary(activeMode, { force: true });
  }, [activeMode, fetchSummary]);

  const isLoadingActive = loadingMode === activeMode;
  const activeError = errors[activeMode];
  const activeSummary = summaries[activeMode] ?? "";
  const hasSummary = activeSummary.length > 0;

  const handleCopy = useCallback(async () => {
    if (!hasSummary) return;
    try {
      await navigator.clipboard.writeText(activeSummary);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard may be unavailable (insecure context); fail quietly
    }
  }, [activeSummary, hasSummary]);

  return (
    <Modal open={open} onOpenChange={onOpenChange}>
      <ModalContent size="md">
        <ModalHeader title="Summarize Profile" iconName="IconSparklesTwo" />
        <ModalBody className="flex flex-col gap-4">
          <Tabs
            value={activeMode}
            onValueChange={handleTabChange}
            variant="default"
            size="sm"
          >
            <TabsList className="w-fit max-w-full mb-2 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {MODES.map((m) => (
                <TabsTrigger
                  key={m.value}
                  value={m.value}
                  className="shrink-0"
                  disabled={loadingMode !== null}
                >
                  <Icon name={m.icon} size={14} />
                  {m.label}
                </TabsTrigger>
              ))}
            </TabsList>
            {MODES.map((m) => (
              <TabsContent key={m.value} value={m.value}>
                <SummaryPanel
                  mode={m.value}
                  loading={isLoadingActive && activeMode === m.value}
                  error={m.value === activeMode ? activeError : undefined}
                  summary={summaries[m.value] ?? ""}
                  onRetry={() => void fetchSummary(m.value, { force: true })}
                />
                <span className="text-xs text-muted-foreground mt-0.5">
                  Generated with AI, confirm information.
                </span>
              </TabsContent>
            ))}
          </Tabs>
        </ModalBody>
        <ModalFooter align="between">
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={handleCopy}
            disabled={!hasSummary || isLoadingActive}
            icon={
              <Icon
                name={copied ? "IconCheckmark1" : "IconClipboard"}
                size={16}
              />
            }
          >
            {copied ? "Copied" : "Copy to Clipboard"}
          </Button>
          <Button
            type="button"
            variant="primary"
            size="sm"
            onClick={handleRegenerate}
            disabled={isLoadingActive || !hasSummary}
            icon={
              <Icon
                name={isLoadingActive ? "IconLoadingCircle" : "IconSparklesTwo"}
                size={16}
                className={isLoadingActive ? "animate-spin" : undefined}
              />
            }
          >
            Regenerate
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

interface SummaryPanelProps {
  mode: SummaryMode;
  loading: boolean;
  error: string | undefined;
  summary: string;
  onRetry: () => void;
}

function SummaryPanel({
  mode,
  loading,
  error,
  summary,
  onRetry,
}: SummaryPanelProps) {
  const segments = useMemo(
    () => (summary ? parseSpotlightMarkdown(summary) : []),
    [summary],
  );
  const { resolvedTheme } = useTheme();
  const isLight = resolvedTheme === "light";

  if (error) {
    return (
      <Alert.Container
        role="alert"
        variant="destructive"
        size="lg"
        className="gap-3"
      >
        <Alert.Content>{error || "Unknown Error"}</Alert.Content>
        <Button type="button" variant="secondary" size="sm" onClick={onRetry}>
          Retry
        </Button>
      </Alert.Container>
    );
  }

  return (
    <div
      aria-label={`${mode} summary`}
      className={cn("w-full rounded-md border border-border overflow-hidden")}
      style={{
        backgroundImage: isLight
          ? "linear-gradient(180deg, rgba(124,58,237,0) 0%, rgba(124,58,237,0.06) 100%)"
          : "linear-gradient(180deg, rgba(154,106,237,0) 0%, rgba(154,106,237,0.1) 100%)",
      }}
    >
      <ScrollArea className="max-h-80 w-full">
        <div className="flex p-4 gap-2.5">
          <Icon
            name="IconSparklesTwo"
            className="size-4 text-[#7c3aed] dark:text-[#D4C3FF] mt-0.5"
          />
          <div className="flex flex-col gap-2.5 items-start w-full text-sm font-normal text-foreground/70 whitespace-pre-wrap">
            <AnimatePresence mode="wait">
              {loading && !summary ? (
                <motion.div
                  key="loading"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.2 }}
                >
                  <SpotlightShimmer label={`Generating ${mode} summary`} />
                </motion.div>
              ) : summary ? (
                <motion.div
                  key="content"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.3 }}
                >
                  <SpotlightTypewriter segments={segments} />
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>
        </div>
      </ScrollArea>
    </div>
  );
}
